import { Entity } from '@api';
import { TableCell, TableHeader } from '@design/components/table';
import { TanStackTableRoot } from '@design/components/tanstack-table/TanStackTable';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useBrandsAndProducts } from '@terminal/react-query-hooks/knowledge-graph/useBrandsAndProducts';
import clsx from 'clsx';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { LoaderCell } from '../../theme-exposure-tab/theme-exposure-table/loader-cell/LoaderCell';
import { RankingMethodologySection } from '../../theme-exposure-tab/theme-exposure-table/ranking-methodology-section/RankingMethodologySection';
import { useBrandAndProductDrawer } from '../useBrandAndProductDrawer';
import styles from './BrandsAndProductsTable.module.scss';
import { getColumns, RANK_COLUMN_ID } from './utils/getColumns';

interface BrandsAndProductsTableProps {
  entity: Entity;
}

export const BrandsAndProductsTable = ({
  entity,
}: BrandsAndProductsTableProps) => {
  const { t } = useTranslation(['widget']);

  const containerRef = useRef<HTMLDivElement>(null);
  const { data, isFetching, error } = useBrandsAndProducts(entity.tag);
  const { openDrawer } = useBrandAndProductDrawer({ entityTag: entity.tag });

  const columns = getColumns({ t });

  const table = useReactTable({
    data: data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (error) {
    throw error;
  }

  return (
    <>
      <div
        ref={containerRef}
        className={clsx(styles.tableWrapper, {
          [styles.loading]: isFetching,
          [styles.notLoading]: !isFetching,
        })}
      >
        <TanStackTableRoot data-testid="brands-and-products-table">
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <TableHeader
                      key={header.id}
                      className={styles.styledTableHeader}
                      data-table-header
                      alignment="left"
                      isHoverEnabled={false}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </TableHeader>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {isFetching ? (
              <LoaderCell numberOfRows={3} />
            ) : (
              table.getRowModel().rows.map(row => (
                <tr
                  key={row.id}
                  onClick={() => openDrawer(row.original.product)}
                >
                  {row.getVisibleCells().map(cell => {
                    return (
                      <TableCell
                        key={cell.id}
                        className={styles.styledTableCell}
                        data-table-cell
                        alignment="left"
                        isNumber={cell.column.id === RANK_COLUMN_ID}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    );
                  })}
                </tr>
              ))
            )}
            <tr data-testid="footer-row">
              <TableCell
                className={styles.footerRow}
                data-footer-row
                colspan={table.getHeaderGroups()[0].headers.length}
                isNumber={false}
              >
                <RankingMethodologySection rankingType="companyToBrandAndProduct" />
              </TableCell>
            </tr>
          </tbody>
        </TanStackTableRoot>
      </div>
    </>
  );
};
